import { supabase } from '@/lib/supabase';
import type { OptimizedProfile, OptimizedVolunteer } from '@/utils/optimizedQueries';
import type { VolunteersEvent } from './useVolunteersPage';
import type { VolunteerActivity } from '@/pages/Profile/components/LatestActivitySection';

export type OrgMemberManagePayload = {
  membershipId: string;
  organizationId: string;
  volunteerUserId: string;
  /** All event ids owned by the org (certificates query). */
  orgEventIds: string[];
  primaryVolunteer: OptimizedVolunteer;
  primaryEvent: VolunteersEvent;
  eventsIn: { id: string; name: string }[];
  activities: VolunteerActivity[];
  totalHoursSum: number;
  hasVolunteerAssignments: boolean;
  /** Role of this member within the organization. */
  memberRole: 'member' | 'admin';
};

function mapEventRow(row: Record<string, unknown>): VolunteersEvent {
  return {
    id: String(row.id),
    name: String(row.name ?? ''),
    is_active: Boolean(row.is_active),
    image: (row.image as string | null) ?? null,
    location: String(row.location ?? ''),
    start_date: (row.start_date as string | null) ?? null,
    end_date: (row.end_date as string | null) ?? null,
    status: String(row.status ?? ''),
    slots: Number(row.slots ?? 0),
    departments: (row.departments as string[] | null) ?? null,
    time_intervals: (row.time_intervals as string[] | null) ?? null,
    is_public: Boolean(row.is_public),
    created_by: String(row.created_by ?? ''),
    created_at: String(row.created_at ?? ''),
  };
}

function rowToOptimizedVolunteer(
  row: {
    id: string;
    volunteer_id: string;
    event_id: string;
    hours: number | null;
    department: string | null;
    time_slots: string | string[] | null;
    is_active: boolean | null;
    created_at: string;
  },
  profile: OptimizedProfile | undefined,
  eventMini: { id: string; name: string; image: string | null } | undefined
): OptimizedVolunteer {
  const deptStr = row.department ?? '';
  const departments = deptStr
    ? deptStr.split(',').map((d) => d.trim()).filter(Boolean)
    : [];
  const slots = row.time_slots;
  const time_slots = Array.isArray(slots)
    ? slots
    : typeof slots === 'string' && slots
      ? [slots]
      : [];
  return {
    id: row.id,
    volunteer_id: row.volunteer_id,
    event_id: row.event_id,
    status: row.is_active ? 'active' : 'inactive',
    departments,
    time_slots,
    created_at: row.created_at,
    hours: row.hours ?? 0,
    certificateCount: 0,
    profiles: profile,
    events: eventMini,
  };
}

function mapVolunteerRowToActivity(
  row: {
    id: string;
    department: string | null;
    hours: number | null;
    time_slots: string | string[] | null;
    created_at: string;
  },
  eventRow: VolunteersEvent
): VolunteerActivity {
  const months: Record<number, string> = {
    0: 'IAN', 1: 'FEB', 2: 'MAR', 3: 'APR',
    4: 'MAI', 5: 'IUN', 6: 'IUL', 7: 'AUG',
    8: 'SEP', 9: 'OCT', 10: 'NOV', 11: 'DEC',
  };

  let dateDisplay = 'Ongoing';
  if (eventRow.start_date && eventRow.end_date) {
    const startDate = new Date(eventRow.start_date);
    const endDate = new Date(eventRow.end_date);
    const startDay = startDate.getDate();
    const startMonth = months[startDate.getMonth()];
    const endDay = endDate.getDate();
    const endMonth = months[endDate.getMonth()];

    if (
      startDate.getDate() === endDate.getDate() &&
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getFullYear() === endDate.getFullYear()
    ) {
      dateDisplay = `${startDay} ${startMonth}`;
    } else {
      dateDisplay = `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
    }
  }

  const rawTimeSlots = row.time_slots;
  const timeSlots = Array.isArray(rawTimeSlots)
    ? rawTimeSlots.filter(Boolean).join(', ')
    : rawTimeSlots ?? 'TBD';

  return {
    id: row.id,
    title: eventRow.name,
    location: eventRow.location || 'Location TBD',
    date: dateDisplay,
    image:
      typeof eventRow.image === 'string' && eventRow.image.trim() !== ''
        ? eventRow.image
        : null,
    department: row.department || 'General',
    hours: row.hours ?? 0,
    timeSlots,
    status: eventRow.status || 'active',
    created_at: row.created_at,
    events: {
      id: eventRow.id,
      name: eventRow.name,
      location: eventRow.location,
      image: eventRow.image,
      start_date: eventRow.start_date,
      end_date: eventRow.end_date,
      status: eventRow.status,
      slots: eventRow.slots,
      departments: eventRow.departments ?? [],
      time_intervals: eventRow.time_intervals ?? [],
      is_public: eventRow.is_public,
      created_by: eventRow.created_by,
      created_at: eventRow.created_at,
    },
  };
}

export async function fetchOrgMemberManageContext(
  organizationId: string,
  volunteerUserId: string,
  membershipId: string
): Promise<OrgMemberManagePayload | null> {
  const [profileRes, orgEventsRes, membershipRes] = await Promise.all([
    supabase
      .from('profiles')
      .select('id, first_name, last_name, profile_image, role')
      .eq('id', volunteerUserId)
      .maybeSingle(),
    supabase
      .from('events')
      .select(
        'id, name, is_active, image, location, start_date, end_date, status, slots, departments, time_intervals, is_public, created_by, created_at'
      )
      .eq('created_by', organizationId),
    supabase
      .from('organization_memberships')
      .select('role')
      .eq('id', membershipId)
      .maybeSingle(),
  ]);

  const profile = profileRes.data as OptimizedProfile | undefined;
  const orgEvents = orgEventsRes.data;
  const evErr = orgEventsRes.error;
  if (evErr) return null;
  const memberRole: 'member' | 'admin' =
    (membershipRes.data?.role as 'member' | 'admin' | undefined) ?? 'member';

  const eventIds = (orgEvents ?? []).map((e) => e.id);
  if (eventIds.length === 0) {
    const syntheticEvent: VolunteersEvent = {
      id: '00000000-0000-0000-0000-000000000001',
      name: 'Your organization',
      is_active: true,
      image: null,
      location: '',
      start_date: null,
      end_date: null,
      status: '',
      slots: 0,
      departments: null,
      time_intervals: null,
      is_public: true,
      created_by: organizationId,
      created_at: new Date().toISOString(),
    };
    const syntheticVolunteer: OptimizedVolunteer = {
      id: '',
      volunteer_id: volunteerUserId,
      event_id: syntheticEvent.id,
      status: 'active',
      departments: [],
      time_slots: [],
      created_at: new Date().toISOString(),
      hours: 0,
      certificateCount: 0,
      profiles: profile,
      events: undefined,
    };
    return {
      membershipId,
      organizationId,
      volunteerUserId,
      orgEventIds: [],
      primaryVolunteer: syntheticVolunteer,
      primaryEvent: syntheticEvent,
      eventsIn: [],
      activities: [],
      totalHoursSum: 0,
      hasVolunteerAssignments: false,
      memberRole,
    };
  }

  const { data: volRows } = await supabase
    .from('volunteers')
    .select('id, volunteer_id, event_id, hours, department, time_slots, is_active, created_at')
    .eq('volunteer_id', volunteerUserId)
    .in('event_id', eventIds)
    .eq('is_active', true);

  const rows = volRows ?? [];
  const totalHoursSum = rows.reduce((s, v) => s + (v.hours ?? 0), 0);

  const volEventIds = new Set(rows.map((v) => v.event_id));
  const eventsIn: { id: string; name: string }[] = (orgEvents ?? [])
    .filter((e) => volEventIds.has(e.id))
    .map((e) => ({ id: e.id, name: e.name ?? 'Event' }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const eventMap = new Map(
    (orgEvents ?? []).map((e) => [e.id, mapEventRow(e as Record<string, unknown>)])
  );
  const activities = rows
    .map((row) => {
      const eventRow = eventMap.get(row.event_id);
      if (!eventRow) return null;
      return mapVolunteerRowToActivity(
        row as Parameters<typeof mapVolunteerRowToActivity>[0],
        eventRow
      );
    })
    .filter((activity): activity is VolunteerActivity => activity != null)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const firstRow = rows[0];
  let primaryVolunteer: OptimizedVolunteer;
  let primaryEvent: VolunteersEvent;

  if (firstRow) {
    const evFull = (orgEvents ?? []).find((e) => e.id === firstRow.event_id);
    if (!evFull) return null;
    primaryEvent = mapEventRow(evFull as Record<string, unknown>);
    primaryVolunteer = rowToOptimizedVolunteer(
      firstRow as Parameters<typeof rowToOptimizedVolunteer>[0],
      profile,
      { id: evFull.id, name: evFull.name, image: evFull.image }
    );
  } else {
    const fallbackEv = orgEvents![0];
    primaryEvent = mapEventRow(fallbackEv as Record<string, unknown>);
    primaryVolunteer = {
      id: '',
      volunteer_id: volunteerUserId,
      event_id: primaryEvent.id,
      status: 'active',
      departments: [],
      time_slots: [],
      created_at: new Date().toISOString(),
      hours: 0,
      certificateCount: 0,
      profiles: profile,
      events: { id: primaryEvent.id, name: primaryEvent.name, image: primaryEvent.image },
    };
  }

  return {
    membershipId,
    organizationId,
    volunteerUserId,
    orgEventIds: eventIds,
    primaryVolunteer,
    primaryEvent,
    eventsIn,
    activities,
    totalHoursSum,
    hasVolunteerAssignments: rows.length > 0,
    memberRole,
  };
}
